import React, { useState, useEffect, useRef, useCallback } from 'react';

const ANALYSIS_TYPE_OPTIONS = [
  'Stopping Sight Distance (SSD)',
  'Passing Sight Distance (PSD)',
  'Flat Spot Analysis (FSA)',
  'Intersection Sight Distance (ISD)',
  'Sunglare Analysis',
  'Guardrails Warrant',
  'Horizontal Alignment',
  'Existing Guardrails',
  'Embankment Analysis',
  'Clear Zone Analysis',
];

/** Path length in miles from GeoJSON coordinates [[lng, lat], ...] or waypoints [{ lat, lng }] */
export function getPathLengthMiles(coordsOrWaypoints) {
  if (!coordsOrWaypoints || coordsOrWaypoints.length < 2) return null;
  const pts = coordsOrWaypoints.map((p) =>
    Array.isArray(p) ? { lat: p[1], lng: p[0] } : { lat: p.lat, lng: p.lng }
  );
  const R = 6371000; // Earth radius in meters
  const M_TO_MI = 0.000621371;
  let totalM = 0;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    const dLat = ((b.lat - a.lat) * Math.PI) / 180;
    const dLng = ((b.lng - a.lng) * Math.PI) / 180;
    const x = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
    totalM += R * c;
  }
  return totalM * M_TO_MI;
}

/** Collapsible waypoints list for one segment (used inside Path Creator panel) */
export default function PathsPanelWaypoints({
  segmentIndex,
  segmentLabel: segmentLabelProp,
  waypoints = [],
  pathData,
  onRemoveWaypoint,
  onClearPath,
  isActive,
  onSelect,
  segmentColor,
  isPolylineVisible = true,
  onTogglePolylineVisibility,
  onGoToSegment,
  hasPathData = false,
  onDeleteSegment,
}) {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [selectedAnalysisType, setSelectedAnalysisType] = useState(ANALYSIS_TYPE_OPTIONS[0]);
  const typeSelectRef = useRef(null);

  const updateSelectWidthToLongest = useCallback(() => {
    const el = typeSelectRef.current;
    if (!el || ANALYSIS_TYPE_OPTIONS.length === 0) return;
    const computed = window.getComputedStyle(el);
    const span = document.createElement('span');
    span.style.cssText = [
      'position:absolute;visibility:hidden;white-space:nowrap;',
      `font-family:${computed.fontFamily};`,
      `font-size:${computed.fontSize};`,
      `font-weight:${computed.fontWeight};`,
      `letter-spacing:${computed.letterSpacing};`,
    ].join('');
    let maxWidth = 0;
    ANALYSIS_TYPE_OPTIONS.forEach((text) => {
      span.textContent = text;
      document.body.appendChild(span);
      const w = span.getBoundingClientRect().width;
      document.body.removeChild(span);
      if (w > maxWidth) maxWidth = w;
    });
    const padding = 8 + 8;
    const arrow = 24;
    el.style.setProperty('width', `${Math.ceil(maxWidth) + padding + arrow}px`, 'important');
  }, []);

  useEffect(() => {
    updateSelectWidthToLongest();
  }, [updateSelectWidthToLongest]);

  const formatCoord = (val) => Number(val).toFixed(6);
  const waypointsList = Array.isArray(waypoints) ? waypoints : [];

  const pathLengthMi = pathData?.geometry?.coordinates?.length >= 2
    ? getPathLengthMiles(pathData.geometry.coordinates)
    : getPathLengthMiles(waypointsList);
  const lengthLabel = pathLengthMi != null ? ` · ${pathLengthMi < 0.1 ? (pathLengthMi * 5280).toFixed(0) + ' ft' : pathLengthMi.toFixed(2) + ' mi'}` : '';
  const fallbackSegmentLabel =
    segmentIndex != null ? `Path ${segmentIndex + 1}` : '';
  const segmentLabel = segmentLabelProp || fallbackSegmentLabel;

  return (
    <div className={`paths-panel-waypoints ${isCollapsed ? 'paths-panel-waypoints-collapsed' : ''} ${isActive ? 'paths-panel-waypoints-active' : ''}`}>
      <div className="paths-panel-waypoints-header">
        <div
          role="button"
          tabIndex={0}
          className={`paths-panel-waypoints-header-toggle ${isActive ? 'paths-panel-waypoints-header-toggle-active' : ''}`}
          onClick={() => onSelect?.()}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect?.(); } }}
          aria-pressed={isActive}
          aria-label={`Select ${segmentLabel || 'segment'}`}
        >
          <button
            type="button"
            className="paths-panel-waypoints-chevron-btn"
            onClick={(e) => { e.stopPropagation(); setIsCollapsed((c) => !c); }}
            aria-expanded={!isCollapsed}
            aria-controls={`paths-panel-waypoints-body-${segmentIndex ?? 0}`}
            aria-label={isCollapsed ? 'Expand segment' : 'Collapse segment'}
          >
            <i className={`fas fa-chevron-right paths-panel-waypoints-chevron ${!isCollapsed ? 'paths-panel-waypoints-chevron-open' : ''}`} aria-hidden />
          </button>
          <div className="paths-panel-waypoints-header-toggle-content">
          <div className="paths-panel-waypoints-header-toggle-top">
            <span className="paths-panel-waypoints-title">{segmentLabel ? `${segmentLabel} · ` : ''} ({waypointsList.length}){lengthLabel}</span>
            {onGoToSegment && (
              <span
                role="button"
                tabIndex={0}
                className={`paths-panel-waypoints-goto-btn ${!hasPathData ? 'paths-panel-waypoints-goto-btn-disabled' : ''}`}
                onClick={(e) => { e.stopPropagation(); if (hasPathData) onGoToSegment(); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); if (hasPathData) onGoToSegment(); } }}
                title={hasPathData ? 'Go to segment on map' : 'Draw path to go to'}
                aria-label="Go to segment on map"
                aria-disabled={!hasPathData}
              >
                <i className="fa-solid fa-location-crosshairs" aria-hidden />
              </span>
            )}
            <button
              type="button"
              className="paths-panel-waypoints-eye-btn"
              onClick={(e) => { e.stopPropagation(); onTogglePolylineVisibility?.(); }}
              title={isPolylineVisible ? 'Hide segment on map' : 'Show segment on map'}
              aria-label={isPolylineVisible ? 'Hide polyline' : 'Show polyline'}
            >
              <i className={`fas ${isPolylineVisible ? 'fa-eye' : 'fa-eye-slash'}`} aria-hidden />
            </button>
            <i
              className="fa-solid fa-location-dot paths-panel-waypoints-header-toggle-dot"
              style={{ color: segmentColor }}
              title="Segment color on map"
              aria-hidden
            />
          </div>
          <div className="paths-panel-waypoints-type-select-row-wrap">
            <div className="paths-panel-waypoints-type-select-row">
              <select
                ref={typeSelectRef}
                className={`paths-panel-waypoints-type-select ${!isActive ? 'paths-panel-waypoints-type-select-disabled' : ''}`}
                value={selectedAnalysisType}
                disabled={!isActive}
                onChange={(e) => {
                  e.stopPropagation();
                  setSelectedAnalysisType(e.target.value);
                }}
                onClick={(e) => e.stopPropagation()}
                aria-label="Analysis type"
                title={isActive ? 'Analysis type' : 'Select this segment to change analysis type'}
              >
                {ANALYSIS_TYPE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            {onDeleteSegment && (
              <button
                type="button"
                className="paths-panel-waypoints-delete-segment-btn"
                onClick={(e) => { e.stopPropagation(); onDeleteSegment?.(segmentIndex); }}
                title="Delete segment"
                aria-label="Delete segment"
              >
                <i className="fa-solid fa-trash-can" aria-hidden />
              </button>
            )}
          </div>
          </div>
        </div>
      </div>
      <div id={`paths-panel-waypoints-body-${segmentIndex ?? 0}`} className="paths-panel-waypoints-body">
        {pathLengthMi != null && (
          <p className="paths-panel-segment-stats">
            Distance: {pathLengthMi < 0.1 ? (pathLengthMi * 5280).toFixed(0) + ' ft' : pathLengthMi.toFixed(2) + ' mi'}
            {' · '}
            {waypointsList.length} waypoint{waypointsList.length !== 1 ? 's' : ''}
          </p>
        )}
        {waypointsList.map((wp, i) => (
          <div key={i} className="paths-waypoint-item">
            <div className="paths-waypoint-info">
              <span className="paths-waypoint-number">{i + 1}.</span>
              <span className="paths-waypoint-coords">
                {wp.address ?? `${formatCoord(wp.lat)}, ${formatCoord(wp.lng)}`}
              </span>
            </div>
            <button
              type="button"
              className="paths-waypoint-remove"
              onClick={() => onRemoveWaypoint?.(i)}
              title="Remove waypoint"
              aria-label="Remove waypoint"
            >
              <i className="fas fa-times" />
            </button>
          </div>
        ))}
        {pathData && pathData.properties?.pointsEvery10Feet?.length > 0 && (
              <p>
                {pathData.properties.pointsEvery10Feet.length} points every 10 ft.
                {' '}
                <button
                  type="button"
                  className="paths-panel-copy-coords"
                  onClick={() => {
                    const text = pathData.properties.pointsEvery10Feet
                      .map((p, i) => `${i + 1}. ${Number(p.lat).toFixed(6)}, ${Number(p.lng).toFixed(6)}`)
                      .join('\n');
                    navigator.clipboard.writeText(text);
                  }}
                >
                  Copy 10-ft coordinates
                </button>
              </p>
            )}
      </div>
    </div>
  );
}
